import React, { useCallback, useState } from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import { type DocNode } from '@atlaskit/adf-schema';
import Button from '@atlaskit/button/new';
import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box } from '@atlaskit/primitives/compiled';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import { type EditorSuggestionRichContent } from '@atlaskit/rovo-triggers/types';
import Tooltip from '@atlaskit/tooltip';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { useChatContextEditorContext } from '@atlassian/conversation-assistant-store/controllers/chat-context/store';
import { useChartApiService } from '@atlassian/conversation-assistant-viz/services/chart-api-service';

import { useGlobalConfig } from '../../../../common/utils/global-config/useGlobalConfig';
import { replaceChartPlaceholdersWithSmartLinks } from '../../../../common/utils/replace-chart-placeholders';
import { buildEditorInsertAdfFragment } from '../../../../ui/chat-history/store-assistant/copy-response/buildEditorInsertAdfFragment';

import messages from './messages';

const LUMINA_PLACEHOLDER_RE = /\[\^lumina(?:-\d+)?\^\]/;

export type InsertReplaceButtonProps = {
	content: string | DocNode;
	agentId?: string;
	onClick?: (mode: 'insert' | 'replace') => void;
	markdownData?: Record<string, string>;
};

export const InsertReplaceButton = ({
	content,
	agentId,
	onClick,
	markdownData,
}: InsertReplaceButtonProps): React.JSX.Element | null => {
	di(useChatContextEditorContext, usePublish, useChartApiService);
	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();
	const { productKey, siteId } = useGlobalConfig();
	const publish = usePublish('ai-mate');
	const chartApiService = useChartApiService(siteId);
	const [context] = useChatContextEditorContext();
	const [isProcessing, setIsProcessing] = useState(false);
	const isSelection = !!context?.selection?.content?.length;
	const mode = isSelection ? 'replace' : 'insert';
	const isDisabled = !context?.document;

	const getInsertButtonLabel = useCallback(() => {
		switch (productKey) {
			case 'confluence':
				return messages.confluenceInsertButtonLabel;
			case 'jira':
				return messages.jiraInsertButtonLabel;
			default:
				return messages.genericInsertButtonLabel;
		}
	}, [productKey]);

	const handleClick = useCallback(async () => {
		let finalContent = content.toString();

		const isChartPath = !!markdownData?.['chart-0'] && finalContent.includes('[^chart^]');
		let richContent: EditorSuggestionRichContent | undefined;

		if (
			!isChartPath &&
			markdownData &&
			LUMINA_PLACEHOLDER_RE.test(finalContent) &&
			fg('kd_lumina_insert_replace_response_as_adf')
		) {
			const richAdfContent = buildEditorInsertAdfFragment(finalContent, markdownData);
			if (richAdfContent) {
				richContent = {
					type: 'text/adf',
					version: 1,
					content: richAdfContent,
				};
			}
		}

		if (isChartPath) {
			setIsProcessing(true);

			sendAnalyticsEvent({
				action: 'insertedIntoPage',
				actionSubjectId: 'vizPluginChart',
				eventType: 'track',
				attributes: {
					mode,
					productKey,
				},
			});

			finalContent = await replaceChartPlaceholdersWithSmartLinks(
				finalContent,
				markdownData,
				chartApiService,
			);
		}
		publish({
			type: 'editor-suggestion',
			source: 'ai-mate',
			data: {
				mode,
				content: finalContent,
				agentId,
				...(richContent ? { richContent } : {}),
			},
		});

		onClick?.(mode);
		setIsProcessing(false);
	}, [
		publish,
		mode,
		content,
		agentId,
		onClick,
		markdownData,
		chartApiService,
		sendAnalyticsEvent,
		productKey,
	]);

	const onButtonHovered = useCallback(() => {
		sendAnalyticsEvent({
			action: 'hovered',
			actionSubjectId: 'improvedInsertReplaceButton',
			attributes: {
				isDisabled,
			},
		});
	}, [sendAnalyticsEvent, isDisabled]);

	if (!context?.document || context?.isViewMode) {
		return null;
	}

	return (
		<Box onMouseEnter={onButtonHovered}>
			<Tooltip
				position="top"
				content={formatMessage(messages.tooltipMessage)}
				canAppear={() => isDisabled}
			>
				<Button
					isDisabled={isDisabled}
					isLoading={isProcessing}
					onClick={(_, e) => {
						sendAnalyticsEvent(
							e.update({
								actionSubjectId: 'improvedInsertReplaceButton',
								attributes: {
									mode: isSelection ? 'replace' : 'insert',
								},
							}),
						);

						handleClick();
					}}
					spacing="compact"
					appearance="subtle"
					testId="InsertReplaceButton"
				>
					{formatMessage(isSelection ? messages.replaceButtonLabel : getInsertButtonLabel())}
				</Button>
			</Tooltip>
		</Box>
	);
};
